import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Box,
  IndexTable,
  InlineStack,
  Select,
  Spinner,
  Text,
  useBreakpoints,
} from '@shopify/polaris';
import { useAxiosInstance } from '../../context/AxiosContext';
import { usePromotions, APPS, BANNERS } from './api';
import StatusBadge from './StatusBadge';
import AppTags from './AppTags';

const STATE_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Published', value: 'published' },
  { label: 'Unpublished', value: 'unpublished' },
  { label: 'Archived', value: 'archived' },
];

const APP_FILTERS = [
  { label: 'All apps · newest first', value: '' },
  ...APPS.map(({ code, name }) => ({ label: name, value: code })),
];

const BannersList = ({ onEdit }) => {
  const axiosInstance = useAxiosInstance();
  const { smDown } = useBreakpoints();
  const [state, setState] = useState('');
  const [appCode, setAppCode] = useState('');

  const { data: banners, isLoading, isError } = usePromotions({
    resource: BANNERS,
    params: { state, app_code: appCode },
    axiosInstance,
  });

  const rows = (banners || []).map((banner, index) => (
    <IndexTable.Row id={String(banner.id)} key={banner.id} position={index}>
      <IndexTable.Cell>
        <Text as="span" fontWeight="semibold">{banner.title}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell><StatusBadge status={banner.status} /></IndexTable.Cell>
      <IndexTable.Cell><AppTags codes={banner.app_codes} /></IndexTable.Cell>
      <IndexTable.Cell>
        <Button size="slim" onClick={() => onEdit(banner)}>Edit</Button>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  // Filters stay mounted whatever the result is - see the note in CardsList.
  const body = () => {
    if (isLoading) {
      return (
        <Box padding="600">
          <InlineStack align="center"><Spinner accessibilityLabel="Loading featured banners" /></InlineStack>
        </Box>
      );
    }

    if (isError) {
      return (
        <Box padding="400">
          <Banner tone="critical">Could not load featured banners. Refresh to try again.</Banner>
        </Box>
      );
    }

    if (!banners?.length) {
      return (
        <Box padding="600">
          <BlockStack gap="100" inlineAlign="center">
            <Text as="p" fontWeight="medium">Nothing matches this filter</Text>
            <Text as="p" tone="subdued">Try a different status or app.</Text>
          </BlockStack>
        </Box>
      );
    }

    return (
      <IndexTable
        resourceName={{ singular: 'featured banner', plural: 'featured banners' }}
        itemCount={banners.length}
        selectable={false}
        condensed={smDown}
        headings={[
          { title: 'Banner' },
          { title: 'Status' },
          { title: 'Shown in' },
          { title: 'Actions' },
        ]}
      >
        {rows}
      </IndexTable>
    );
  };

  return (
    <BlockStack gap="400">
      <InlineStack gap="300">
        <Select label="Status" labelInline options={STATE_FILTERS} value={state} onChange={setState} />
        <Select label="Showing order for" labelInline options={APP_FILTERS} value={appCode} onChange={setAppCode} />
      </InlineStack>

      <Card padding="0">{body()}</Card>
    </BlockStack>
  );
};

BannersList.propTypes = {
  onEdit: PropTypes.func.isRequired,
};

export default BannersList;
